// Apple Wallet Service for generating ticket passes
class AppleWalletService {
  constructor() {
    // Using Railway deployment for Apple Wallet server
    this.baseUrl = 'https://considerate-compassion-production.up.railway.app';
  }

  /**
   * Generate and download an Apple Wallet ticket pass
   * @param {Object} bookingData - The booking information
   * @param {string} bookingData.bookingReference - Unique booking reference
   * @param {string} bookingData.eventName - Name of the event/experience
   * @param {Date} bookingData.selectedDate - Date of the visit
   * @param {Object} bookingData.selectedTimeSlot - Time slot information
   * @param {Array} bookingData.selectedTickets - Array of selected tickets
   * @param {Object} bookingData.customerDetails - Customer information
   * @returns {Promise<string>} - The download URL or blob for the pass
   */
  async generateTicketPass(bookingData) {
    try {
      const {
        bookingReference,
        eventName = 'Belvoir Castle Visit',
        selectedDate,
        selectedTimeSlot,
        selectedTickets,
        customerDetails
      } = bookingData;

      // Format the date and time for the pass
      // Format date in local timezone to avoid UTC conversion issues
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const eventDate = `${year}-${month}-${day}`; // YYYY-MM-DD format in local timezone
      const eventTime = this.formatTimeSlot(selectedTimeSlot);
      
      // Calculate total quantity
      const quantity = selectedTickets
        .filter(ticket => ticket.quantity > 0)
        .reduce((total, ticket) => total + ticket.quantity, 0);

      // Always use direct Railway URL for all browsers (simpler and works like membership pass)

      return this.generateDirectDownloadUrl({
        bookingReference,
        eventName,
        eventDate,
        eventTime,
        quantity: quantity.toString(),
        customerName: `${customerDetails.firstName} ${customerDetails.lastName}`
      });

    } catch (error) {

      throw new Error(`Failed to generate Apple Wallet pass: ${error.message}`);
    }
  }

  /**
   * Format time slot for display
   * @param {Object|string} timeSlot - Time slot information
   * @returns {string} - Formatted time string
   */
  formatTimeSlot(timeSlot) {
    if (!timeSlot) return 'Not specified';
    
    if (typeof timeSlot === 'string') {
      return timeSlot;
    }
    
    if (timeSlot.time) {
      return timeSlot.time;
    }
    
    return 'Not specified';
  }

  /**
   * Download the generated pass file
   * @param {Blob} passBlob - The .pkpass file blob
   * @param {string} filename - The filename for the download
   */
  downloadPass(passBlob, filename = 'BelvoirCastle_Ticket.pkpass') {
    try {
      // Create a download link
      const url = window.URL.createObjectURL(passBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      // Trigger the download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {

      throw new Error('Failed to download the Apple Wallet pass');
    }
  }

  /**
   * Check if Apple Wallet is supported on the current device
   * @returns {boolean} - True if Apple Wallet is likely supported
   */
  isAppleWalletSupported() {
    // Check if we're on iOS/macOS Safari or if the device supports .pkpass files
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isMacOS = /macintosh|mac os x/.test(userAgent);
    const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);
    
    // Apple Wallet is primarily supported on iOS devices
    // Also works on macOS Safari to some extent
    return isIOS || (isMacOS && isSafari);
  }

  /**
   * Check if the current browser is Safari on mobile/iOS
   * @returns {boolean} - True if Safari mobile
   */
  isSafariMobile() {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);
    
    return isIOS && isSafari;
  }

  /**
   * Generate a direct download URL for Safari mobile
   * @param {Object} passData - Pass data for URL generation
   * @returns {string} - Direct download URL
   */
  generateDirectDownloadUrl(passData) {
    const {
      bookingReference,
      eventName,
      eventDate,
      eventTime,
      quantity
    } = passData;

    // Encode parameters for URL
    const encodedEventName = encodeURIComponent(eventName);
    const encodedEventDate = encodeURIComponent(eventDate);
    const encodedEventTime = encodeURIComponent(eventTime);
    const encodedQuantity = encodeURIComponent(quantity || '1');

    // Use the GET endpoint for direct downloads (Safari-friendly)
    const encodedCustomerName = encodeURIComponent(passData.customerName || 'Guest');
    const downloadUrl = `${this.baseUrl}/api/tickets/download/${bookingReference}/${encodedEventName}/${encodedEventDate}/${encodedEventTime}?quantity=${encodedQuantity}&customerName=${encodedCustomerName}`;

    return downloadUrl;
  }

  /**
   * Get the appropriate button text based on device support
   * @returns {string} - Button text
   */
  getButtonText() {
    return this.isAppleWalletSupported() 
      ? 'Add to Apple Wallet' 
      : 'Download Ticket Pass';
  }

  /**
   * Get the server health status
   * @returns {Promise<boolean>} - True if server is running
   */
  async checkServerHealth() {
    try {
      const response = await fetch(`${this.baseUrl}/`);
      return response.ok;
    } catch (error) {

      return false;
    }
  }
}

// Export a singleton instance
const appleWalletService = new AppleWalletService();
export default appleWalletService;
